import { InputNumber, Modal, Typography } from 'antd'
import { computeCalculatedFutureCorrosionAllowance } from 'features/libra/stepComponents/ffs/binningCalculations'
import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Label = styled.span`
  width: 13rem;
  font-weight: 500;
`

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.forecast.border2 ?? '#444'};
  border-radius: 0.5rem;
`

/**
 * "Calculate FCA (CR * time)" helper modal for the binning step. Local
 * state only -- the applied `GeckoMeasurement` is handed back through
 * `onApply` and persists via the step's own save flow.
 */
export const FcaCalculatorModal: FC<{
  open: boolean
  lengthUnit: GeckoMeasurementUnit
  onClose: () => void
  onApply: (value: GeckoMeasurement) => void
}> = ({ open, lengthUnit, onClose, onApply }) => {
  const [corrosionRate, setCorrosionRate] = useState<number | null>(null)
  const [years, setYears] = useState<number | null>(null)

  const result = useMemo(
    () =>
      computeCalculatedFutureCorrosionAllowance({
        corrosionRate:
          corrosionRate !== null
            ? GeckoMeasurement.CreateFromValue(
                corrosionRate,
                GeckoMeasurementUnit.MillimetersPerYear
              )
            : null,
        years:
          years !== null
            ? GeckoMeasurement.CreateFromValue(
                years,
                GeckoMeasurementUnit.Years
              )
            : null,
      }),
    [corrosionRate, years]
  )

  return (
    <Modal
      title='Calculate FCA (CR * time)'
      open={open}
      onCancel={onClose}
      onOk={() => result && onApply(result)}
      okText='Use Value'
      okButtonProps={{ disabled: result === null }}>
      <SectionBlock>
        <FieldRow>
          <Label>Corrosion rate (CR)</Label>
          <InputNumber
            value={corrosionRate}
            onChange={(v) => setCorrosionRate(typeof v === 'number' ? v : null)}
            step={0.01}
            min={0}
            addonAfter='mm/yr'
          />
        </FieldRow>
        <FieldRow>
          <Label>Time period</Label>
          <InputNumber
            value={years}
            onChange={(v) => setYears(typeof v === 'number' ? v : null)}
            step={0.5}
            min={0}
            addonAfter='years'
          />
        </FieldRow>
        <FieldRow>
          <Label>FCA = CR * time</Label>
          <Typography.Text strong>
            {result !== null
              ? `${result.convertTo(lengthUnit).num.toFixed(3)} ${lengthUnit.id}`
              : '--'}
          </Typography.Text>
        </FieldRow>
      </SectionBlock>
    </Modal>
  )
}
